import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
// import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'wouter';

import StyledButton from '../buttons/StyledButton';
import GameTimer from '../partials/GameTimer';
import RhoneAcademyTimeoutAnimation from './RhoneAcademyTimeoutAnimation';
import Symbols from './Symbols';

import styles from '../../../styles/animations/rhoneacademy-feedback.module.scss';

import correctAnswerImg from '../../../img/rhoneacademy/feedback/correct-answer-header.svg';
import grapeBad1Img from '../../../img/rhoneacademy/feedback/grape-bad-1.svg';
import grapeBad2Img from '../../../img/rhoneacademy/feedback/grape-bad-2.svg';
import grapeBad3Img from '../../../img/rhoneacademy/feedback/grape-bad-3.svg';
import grapeGoodImg from '../../../img/rhoneacademy/feedback/grape-good.svg';
import backIconImg from '../../../img/rhoneacademy/icons/back.svg';

interface RhoneAcademyFeedbackProps {
    className?: string;
    valid?: boolean;
    value?: boolean;
    answerDuration?: number;
    answerPoints?: number;
    validAnswerText?: string;
    explanation?: string;
    gameId?: string;
    questionsGroupId?: string;
    onAnimationComplete?: () => void;
}

function RhoneAcademyFeedback({
    className = null,
    valid = null,
    value = null,
    answerDuration = null,
    answerPoints = null,
    validAnswerText = null,
    explanation = null,
    onAnimationComplete = null,
    gameId = null,
    questionsGroupId = null,
}: RhoneAcademyFeedbackProps) {
    const url = useUrlGenerator();
    const [, navigate] = useLocation();

    const onNextQuestionClicked = useCallback(() => {
        if (onAnimationComplete !== null) {
            onAnimationComplete();
        }
    }, [onAnimationComplete]);

    const onBackToModuleClicked = useCallback(
        () => navigate(url('play.group', { group: `${gameId}/${questionsGroupId}` })),
        [navigate, url, gameId, questionsGroupId],
    );

    const isCorrectAnswer = valid === true;
    const isIncorrectAnswer = valid === false && value !== null;
    const hasTimedOut = valid === false && value === null;

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.valid]: isCorrectAnswer,
                    [styles.invalid]: isIncorrectAnswer,
                    [styles.timedOut]: hasTimedOut,
                },
                className,
            ])}
        >
            <div className={styles.content}>
                <div className={styles.topBar}>
                    <button type="button" className={styles.back} onClick={onBackToModuleClicked}>
                        <img src={backIconImg} alt="Back to module" />
                        <span>
                            <FormattedMessage
                                defaultMessage="Back to module"
                                description="Rhone Academy - Feedback - Back to module"
                            />
                        </span>
                    </button>
                </div>
                <div className={styles.result}>
                    <div className={styles.text}>
                        {isCorrectAnswer ? (
                            <>
                                <Symbols
                                    images={grapeGoodImg}
                                    animation="rain-up"
                                    coverage={0.7}
                                    size={120}
                                    className={styles.symbols}
                                />
                                <img className={styles.img} src={correctAnswerImg} alt="Correct" />
                                <h1>
                                    <FormattedMessage
                                        defaultMessage="Excellent!"
                                        description="Rhone Academy - Feedback title"
                                    />
                                </h1>
                                <h2>
                                    <FormattedMessage
                                        defaultMessage="You're a true expert!"
                                        description="Rhone Academy - Feedback subtitle"
                                    />
                                </h2>
                            </>
                        ) : null}
                        {isIncorrectAnswer ? (
                            <>
                                <Symbols
                                    images={[grapeBad1Img, grapeBad2Img, grapeBad3Img]}
                                    animation="rain-down"
                                    coverage={0.7}
                                    size={120}
                                    className={styles.symbols}
                                />
                                <h1>
                                    <FormattedMessage
                                        defaultMessage="Not quite..."
                                        description="Rhone Academy - Feedback title"
                                    />
                                </h1>
                                <h2>
                                    <FormattedMessage
                                        defaultMessage="You'll get the next one right!"
                                        description="Rhone Academy - Feedback subtitle"
                                    />
                                </h2>
                            </>
                        ) : null}
                        {hasTimedOut ? (
                            <>
                                <RhoneAcademyTimeoutAnimation className={styles.img} />
                                <h1>
                                    <FormattedMessage
                                        defaultMessage="Time's up!"
                                        description="Rhone Academy - Feedback title"
                                    />
                                </h1>
                                <h2>
                                    <FormattedMessage
                                        defaultMessage="Unlike wine, our questions shouldn't be aged!"
                                        description="Rhone Academy - Feedback subtitle"
                                    />
                                </h2>
                            </>
                        ) : null}
                    </div>

                    <div className={styles.stats}>
                        {answerDuration !== null ? (
                            <GameTimer
                                className="still"
                                started={false}
                                duration={hasTimedOut ? 30 : answerDuration}
                            />
                        ) : null}
                        {answerPoints !== null ? (
                            <div className={styles.points}>
                                {answerPoints} point{answerDuration > 1 ? 's' : null}
                            </div>
                        ) : null}
                    </div>

                    {validAnswerText !== null ? (
                        <div className={styles.explanation}>
                            <h4>
                                <FormattedMessage
                                    defaultMessage="Correct answer :"
                                    description="Rhone Academy - Correct answer"
                                />
                            </h4>
                            <h3>{validAnswerText}</h3>
                            {explanation !== null ? <p>{explanation}</p> : null}
                        </div>
                    ) : null}
                </div>

                <StyledButton className={styles.cta} onClick={onNextQuestionClicked}>
                    <FormattedMessage
                        defaultMessage="Next question"
                        description="Rhone Academy Feedback Button"
                    />
                </StyledButton>
            </div>
        </div>
    );
}

// RhoneAcademyFeedback.propTypes = propTypes;

export default RhoneAcademyFeedback;
